import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Hotel Reservation Service
 * Otel rezervasyon API'leri için servis
 */
export const hotelReservationService = {
  /**
   * Rezervasyon bilgilerini al (quote)
   * @param {Object} params - Parametreler
   * @param {string} params.reference - Otel reference değeri (Products[0].Reference)
   * @param {string} params.normalSearchReference - Normal search reference değeri (Products[0].NormalSearchReference)
   * @param {string} params.roomId - Oda ID değeri (RoomTypes[].RoomId)
   * @param {string} params.priceKey - Fiyat anahtarı (RoomTypes[].RoomPrices[].PriceKey)
   * @param {Object} [params.preferences] - Tercihler
   * @param {string} [params.preferences.language] - Dil kodu (default: "tr")
   * @param {string} [params.preferences.currency] - Para birimi (default: "TRY")
   * @param {string} [params.preferences.nationality] - Uyruk (default: "TR")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise<Object>} API response - Response yapısı: { BasketId: number, Products: Array }
   * @returns {number} response.BasketId - Rezervasyon sepet ID'si. Bu değer `/otel/rezervasyon/[BasketId]` sayfasına yönlendirme için kullanılır.
   */
  getReservation: async (
    {
      reference,
      normalSearchReference,
      roomId,
      priceKey,
      preferences = {},
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Preferences için default değerler
    const defaultPreferences = {
      Language: "tr",
      Currency: "TRY",
      Nationality: "TR",
    };

    // Preferences mapping: camelCase -> PascalCase
    const mappedPreferences = {
      Language: preferences.language || defaultPreferences.Language,
      Currency: preferences.currency || defaultPreferences.Currency,
      Nationality: preferences.nationality || defaultPreferences.Nationality,
    };

    // Payload oluştur
    const payload = {
      Reference: reference,
      NormalSearchReference: normalSearchReference,
      PriceKeys: [
        {
          Key: priceKey,
          UnitOfRoom: 1,
        },
      ],
      Nationality: mappedPreferences.Nationality,
      Language: mappedPreferences.Language,
      Currency: mappedPreferences.Currency,
      RoomId: roomId,
    };

    try {
      const response = await request({
        method: HTTP_METHODS.POST,
        url: API_URLS.hotel.reservation.getReservation,
        payload,
        headers,
        hasErrorHandler,
      });

      if (!response?.BasketId) {
        console.warn('⚠️ [RESERVATION SERVICE] WARNING: BasketId not found in response!');
      }

      return response;
    } catch (error) {
      console.error('❌ [RESERVATION SERVICE] API Request Error:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        error: error,
      });
      throw error;
    }
  },

  /**
   * Response'dan BasketId'yi çıkarır
   * @param {Object} response - getReservation fonksiyonundan dönen response
   * @returns {number|null} BasketId değeri veya null
   */
  extractBasketId: (response) => {
    if (!response || typeof response !== 'object') {
      return null;
    }
    return response.BasketId || null;
  },

  /**
   * Sepet bilgilerini al (getBasket)
   * @param {Object} params - Parametreler
   * @param {number} params.basketId - Sepet ID değeri (URL'den alınacak)
   * @param {string} [params.languageCode] - Dil kodu (default: "tr")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getBasket: async (
    {
      basketId,
      languageCode = "tr",
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Payload oluştur
    const payload = {
      BasketId: basketId,
      LanguageCode: languageCode,
      AppVersion: 0,
    };

    // API_URLS kontrolü
    const endpoint = API_URLS?.hotel?.reservation?.getBasket;
    if (!endpoint) {
      console.error('❌ [RESERVATION SERVICE - GET BASKET] API_URLS.hotel.reservation.getBasket is undefined!');
      throw new Error('API endpoint not found: hotel.reservation.getBasket');
    }

    try {
      const response = await request({
        method: HTTP_METHODS.POST,
        url: endpoint, // endpoint değişkenini kullan (yukarıda kontrol edildi)
        payload,
        headers,
        hasErrorHandler,
      });

      return response;
    } catch (error) {
      console.error('❌ [GET BASKET ERROR]', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  /**
   * Rezervasyonu oluştur (reserve)
   * @param {Object} payload - Request payload (form'dan toplanan veriler)
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise<Object>} API response - Response yapısı: { BasketId: number, ... }
   */
  makeReserve: async (
    payload = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // API_URLS kontrolü
    const endpoint = API_URLS?.hotel?.reservation?.getReserve;
    if (!endpoint) {
      console.error('❌ [RESERVATION SERVICE - MAKE RESERVE] API_URLS.hotel.reservation.getReserve is undefined!');
      throw new Error('API endpoint not found: hotel.reservation.getReserve');
    }

    try {
      const response = await request({
        method: HTTP_METHODS.POST,
        url: endpoint,
        payload,
        headers,
        hasErrorHandler,
      });

      return response;
    } catch (error) {
      console.error('❌ [MAKE RESERVE ERROR]', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  /**
   * Otel rezervasyonunu iptal et (hash sayfasından).
   * API adresi apiUrls.js içinde hotel.reservation.cancelHotelReservation ile değiştirilebilir.
   * @param {Object} payload - İstek gövdesi (backend'in beklediği format)
   * @param {number} payload.BasketId - Sepet ID
   * @param {number} payload.ProviderId - Sağlayıcı ID
   * @param {string} payload.ReservationGuid - Rezervasyon GUID
   * @param {number} payload.CancellationPrice - İptal kesinti tutarı
   * @param {string} payload.Currency - Para birimi (örn: "TRY")
   * @param {number} payload.ProviderCancellationPrice - Sağlayıcı iptal tutarı
   * @param {string} payload.ProviderCurrency - Sağlayıcı para birimi
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Özel hata yönetimi
   * @returns {Promise<Object>} API response (örn: { IsSuccess, Message })
   */
  cancelHotelReservation: (payload = {}, headers = {}, hasErrorHandler = false) => {
    const endpoint = API_URLS?.hotel?.reservation?.cancelHotelReservation;
    if (!endpoint) {
      return Promise.reject(new Error("API endpoint not found: hotel.reservation.cancelHotelReservation"));
    }
    return request({
      method: HTTP_METHODS.POST,
      url: endpoint,
      payload,
      headers,
      hasErrorHandler,
    });
  },
};

/**
 * Server-side hotel reservation service
 * Server components'lerde kullanılmak üzere fetch API kullanır
 */
export const hotelReservationServiceServer = {
  /**
   * Sepet bilgilerini al (getBasket) - Server-side
   * @param {Object} params - Parametreler
   * @param {number} params.basketId - Sepet ID değeri
   * @param {string} [params.languageCode] - Dil kodu (default: "tr")
   * @param {Object} headers - HTTP headers (server-side session headers)
   * @returns {Promise<Object|null>} API response veya null (hata durumunda)
   */
  getBasket: async (
    {
      basketId,
      languageCode = "tr",
    } = {},
    headers = {},
  ) => {
    try {
      // API_URLS kontrolü
      const endpoint = API_URLS?.hotel?.reservation?.getBasket;
      if (!endpoint) {
        console.error('❌ [SERVER RESERVATION SERVICE] API_URLS.hotel.reservation.getBasket is undefined!');
        return null;
      }

      const payload = {
        BasketId: basketId,
        LanguageCode: languageCode,
        AppVersion: 0,
      };

      const apiUrl = `${process.env.NEXT_PUBLIC_API_URL}${endpoint}`;
      
      const response = await fetch(apiUrl, {
        method: HTTP_METHODS.POST,
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify(payload),
        cache: 'no-store', // Her istekte fresh data
        redirect: 'follow', // Redirect'leri takip et
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        console.error('❌ [SERVER GET BASKET ERROR]', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500),
        });
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ [SERVER GET BASKET ERROR]', {
        message: error?.message,
        basketId,
      });
      return null;
    }
  },
};
